import React, { useState, useEffect } from "react";
import "./Css/IncidentManagement.css";

function IncidentManagement() {
  const BASE_URL = "https://mondelezbc.vsmglobaltechnologies.com/api/incident";
  const CDN_URL = "https://cdn.vsmglobaltechnologies.com/post-file.php";

  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newIncident, setNewIncident] = useState({
    english: "",
    telugu: "",
    imageFile: null,
  });

  // ✅ Fetch all incidents on mount
  useEffect(() => {
    fetchIncidents();
  }, []);

  const fetchIncidents = async () => {
    try {
      setLoading(true);
      const res = await fetch(BASE_URL);
      const result = await res.json();
      if (res.ok) setIncidents(result.data || []);
    } catch (err) {
      console.error("Fetch error:", err);
      alert("Error fetching incidents");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewIncident((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setNewIncident((prev) => ({ ...prev, imageFile: file }));
  };

  // ✅ Upload image to CDN
  const uploadImageToCDN = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      setUploading(true);
      const res = await fetch(CDN_URL, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        throw new Error("CDN upload failed");
      }

      const result = await res.json();
      // CDN returns { success: true, file: "https://..." }
      if (result.success && result.file) {
        return result.file;
      }
      throw new Error("Invalid CDN response");
    } catch (err) {
      console.error("CDN upload error:", err);
      throw err;
    } finally {
      setUploading(false);
    }
  };

  // ✅ Create new incident
  const addIncident = async () => {
    const { english, telugu, imageFile } = newIncident;

    if (!english) {
      alert("English name is required");
      return;
    }

    if (!imageFile) {
      alert("Image is required");
      return;
    }

    try {
      setLoading(true);

      // Upload image to CDN first
      const imageUrl = await uploadImageToCDN(imageFile);

      // Then create incident with the CDN URL
      const res = await fetch(BASE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          incidentNameEnglish: english,
          incidentNameTelugu: telugu,
          imageUrl: imageUrl,
        }),
      });

      const result = await res.json();
      if (res.ok) {
        alert("Incident added successfully!");
        setIncidents((prev) => [...prev, result.data]);
        setNewIncident({ english: "", telugu: "", imageFile: null });
        // Reset file input
        document.querySelector('input[type="file"]').value = "";
      } else {
        alert(result.message || "Failed to add incident");
      }
    } catch (err) {
      console.error("Add error:", err);
      alert("Error adding incident: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Edit incident
  const editIncident = async (id) => {
    const incident = incidents.find((i) => i._id === id);
    if (!incident) return;

    const english = prompt("Edit English name:", incident.incidentNameEnglish);
    const telugu = prompt("Edit Telugu name:", incident.incidentNameTelugu);
    if (!english) return;

    try {
      const res = await fetch(`${BASE_URL}/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ english, telugu }),
      });
      const result = await res.json();

      if (res.ok) {
        alert("Incident updated!");
        fetchIncidents();
      } else {
        alert(result.message || "Failed to update");
      }
    } catch (err) {
      console.error("Edit error:", err);
      alert("Error updating incident");
    }
  };

  // ✅ Delete incident
  const deleteIncident = async (id) => {
    if (!window.confirm("Are you sure to delete this incident?")) return;
    try {
      const res = await fetch(`${BASE_URL}/${id}`, { method: "DELETE" });
      const result = await res.json();
      if (res.ok) {
        alert("Incident deleted!");
        setIncidents((prev) => prev.filter((i) => i._id !== id));
      } else {
        alert(result.message || "Delete failed");
      }
    } catch (err) {
      console.error("Delete error:", err);
      alert("Error deleting incident");
    }
  };

  return (
    <div className="incident-page">
      <h3>Incident Management</h3>

      <div className="incident-form">
        <input
          type="text"
          name="english"
          placeholder="Incident Name (English)"
          value={newIncident.english}
          onChange={handleChange}
        />
        <input
          type="text"
          name="telugu"
          placeholder="Incident Name (Telugu)"
          value={newIncident.telugu}
          onChange={handleChange}
        />
        <input type="file" accept="image/*" onChange={handleFileChange} />
        <button
          className="btn btn-primary"
          onClick={addIncident}
          disabled={loading || uploading}
        >
          {uploading ? "Uploading..." : loading ? "Saving..." : "Add Incident"}
        </button>
      </div>

      <div className="incident-list">
        {incidents.length === 0 && !loading && (
          <p>No incidents reported yet.</p>
        )}
        {loading && <p>Loading...</p>}
        {incidents.map((inc) => (
          <div className="incident-card" key={inc._id}>
            <img
              src={inc.filePathUrl || inc.imageUrl}
              alt={inc.incidentNameEnglish}
              className="incident-image"
            />
            <div className="incident-info">
              <p>
                <strong>English:</strong> {inc.incidentNameEnglish}
              </p>
              <p>
                <strong>Telugu:</strong> {inc.incidentNameTelugu}
              </p>
            </div>
            <div className="incident-actions">
              <button
                className="btn btn-success"
                onClick={() => editIncident(inc._id)}
              >
                Edit
              </button>
              <button
                className="btn btn-danger"
                onClick={() => deleteIncident(inc._id)}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default IncidentManagement;
